import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getFriendships,
  sendFriendRequest,
  respondToRequest,
  removeFriendship,
  searchUsers
} from '../api/friendships';

function Friends() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [friends, setFriends] = useState([]);
  const [pendingReceived, setPendingReceived] = useState([]);
  const [pendingSent, setPendingSent] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/signin'); return; }
    fetchFriendships();
  }, [isAuthenticated, navigate]);

  const token = () => localStorage.getItem('auth_token');

  const fetchFriendships = async () => {
    setIsLoading(true);
    try {
      const data = await getFriendships(token());
      setFriends(data.friends || []);
      setPendingReceived(data.pending_received || []);
      setPendingSent(data.pending_sent || []);
    } catch {
      setError('Failed to load friends');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimeout);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearchTimeout(setTimeout(async () => {
      const data = await searchUsers(q, token());
      setSearchResults(data.users || []);
    }, 400));
  };

  const handleSendRequest = async (userId) => {
    try {
      await sendFriendRequest(userId, token());
      setSearchResults([]);
      setSearchQuery('');
      fetchFriendships();
    } catch (err) {
      const msg = err.response?.data?.errors?.[0] || 'Could not send request';
      setError(msg);
    }
  };

  const handleAccept = async (friendshipId) => {
    await respondToRequest(friendshipId, 'accepted', token());
    fetchFriendships();
  };

  const handleReject = async (friendshipId) => {
    await respondToRequest(friendshipId, 'rejected', token());
    fetchFriendships();
  };

  const handleRemove = async (friendshipId) => {
    await removeFriendship(friendshipId, token());
    fetchFriendships();
  };

  if (!isAuthenticated) return null;
  if (isLoading) return <p>Loading...</p>;

  return (
    <div className="friends-page">
      <h1>Friends</h1>

      {error && <p className="error">{error}</p>}

      {/* Search */}
      <div className="friends-search">
        <h2>Add a friend</h2>
        <input
          type="text"
          placeholder="Search by email..."
          value={searchQuery}
          onChange={handleSearch}
          className="friends-search__input"
        />
        {searchResults.length > 0 && (
          <ul className="friends-search__results">
            {searchResults.map(user => (
              <li key={user.id} className="friends-search__result-item">
                <span>{user.email}</span>
                <button
                  onClick={() => handleSendRequest(user.id)}
                  className="btn btn-primary btn-sm"
                >
                  Add
                </button>
              </li>
            ))}
          </ul>
        )}
        {searchQuery.length >= 2 && searchResults.length === 0 && (
          <p className="friends-search__no-results">No users found.</p>
        )}
      </div>

      {/* Pending requests received */}
      {pendingReceived.length > 0 && (
        <div className="friends-section">
          <h2>Friend requests <span className="friends-badge">{pendingReceived.length}</span></h2>
          <ul className="friends-list">
            {pendingReceived.map(req => (
              <li key={req.friendship_id} className="friends-list__item">
                <span>{req.email}</span>
                <div className="friends-list__actions">
                  <button onClick={() => handleAccept(req.friendship_id)} className="btn btn-primary btn-sm">Accept</button>
                  <button onClick={() => handleReject(req.friendship_id)} className="btn btn-secondary btn-sm">Decline</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Accepted friends */}
      <div className="friends-section">
        <h2>My friends ({friends.length})</h2>
        {friends.length === 0 ? (
          <p className="friends-empty">No friends yet. Search for someone above!</p>
        ) : (
          <ul className="friends-list">
            {friends.map(friend => (
              <li key={friend.id} className="friends-list__item">
                <Link to={`/friends/${friend.id}`} className="friends-list__name">
                  {friend.email}
                </Link>
                <button
                  onClick={() => handleRemove(friend.friendship_id)}
                  className="btn btn-secondary btn-sm"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pending sent */}
      {pendingSent.length > 0 && (
        <div className="friends-section">
          <h2>Requests sent</h2>
          <ul className="friends-list">
            {pendingSent.map(req => (
              <li key={req.friendship_id} className="friends-list__item">
                <span>{req.email}</span>
                <span className="friends-badge friends-badge--muted">Pending</span>
                <button
                  onClick={() => handleRemove(req.friendship_id)}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Friends;
